import type { ReactNode } from 'react';

import { TextField } from '../TextField/TextField.tsx';
import styles from './AlertPanel.module.css';

export interface AlertPanelProps {
  /** One sentence: what and why, e.g. "SpO₂ 84% is in the critical range." */
  title: string;
  /** What to do, and any evidence. */
  children?: ReactNode;
  /** Clinical danger (a critical value) uses the danger colour; other interruptions don't. */
  danger?: boolean;
  /** The acknowledgement reason; saving waits until it is given. */
  reason: string;
  onReasonChange: (value: string) => void;
  reasonLabel?: string;
  /** Shown under the reason when it is too short to save. */
  reasonError?: string;
}

/**
 * The interruptive alert tier (DESIGN.md §7, CLAUDE.md): an inline panel at the point of
 * action that needs an acknowledgement with a reason before the action goes ahead. The
 * reason is stored with the rule ID so override rates can be measured.
 */
export function AlertPanel({
  title,
  children,
  danger = false,
  reason,
  onReasonChange,
  reasonLabel = 'Reason for continuing',
  reasonError,
}: AlertPanelProps) {
  return (
    <section
      className={styles.panel}
      data-danger={danger || undefined}
      role="alert"
      aria-label={title}
    >
      <p className={styles.title}>{title}</p>
      {children === undefined ? null : <div className={styles.body}>{children}</div>}
      <TextField
        label={reasonLabel}
        value={reason}
        onChange={onReasonChange}
        dense
        {...(reasonError === undefined ? {} : { errorMessage: reasonError })}
      />
    </section>
  );
}
